import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useState, useCallback } from 'react'
import { Stack } from '@sureco/design-system'
import { GridApi, GridReadyEvent } from '@ag-grid-community/core'
import {
  SearchOutlined,
  CalendarOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { Input, Button, App } from '@surecompanies/core_components'
import EnrollmentOutstandingTable from './EnrollmentOutstandingTable'
import EditEnrollmentDatesModal from './EditEnrollmentDatesModal'
import {
  notifyRescheduleResult,
  RescheduleEnrollmentDatesDto,
  useEnrollmentPeriodDates,
} from '../hooks/useEnrollmentPeriodDates'
import { EnrollmentConfiguration } from '../hooks/useEnrollmentConfiguration'
import { Person } from 'types/person'

export default function EnrollmentPeriodDates({
  id,
  enrollment,
}: {
  id: string
  enrollment: EnrollmentConfiguration
}) {
  /* --------------------------------- States --------------------------------- */
  const { queryEnrollmentPeriodDates, rescheduleEnrollmentMutation } =
    useEnrollmentPeriodDates(id)

  const {
    data,
    isLoading: isLoadingQuery,
    isRefetching,
    refetch,
  } = queryEnrollmentPeriodDates(enrollment) || {}
  const { members = [], total = 0 } = data || {}

  const {
    mutate: rescheduleEnrollment,
    isPending,
    error: mutationError,
    reset: resetMutation,
  } = rescheduleEnrollmentMutation()

  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [selectedRows, setSelectedRows] = useState<Person[]>([])
  const [searchText, setSearchText] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  /* -------------------------------- Handlers -------------------------------- */
  const onFilterTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchText(value)
      if (gridApi) {
        gridApi.setQuickFilter(value)
      }
    },
    [gridApi]
  )

  /* --------------------------------- Methods -------------------------------- */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    resetMutation()
  }

  const handleConfirm = (
    dto: Omit<RescheduleEnrollmentDatesDto, 'personIds'>
  ) => {
    rescheduleEnrollment(
      {
        enrollmentId: enrollment.id.toString(),
        dto: {
          ...dto,
          personIds: selectedRows
            .map((row) => row.employeePublicKey)
            .filter((employeeId): employeeId is string => !!employeeId),
        },
      },
      {
        onSuccess: (data) => {
          notifyRescheduleResult(data)
          setSelectedRows([])
          setIsModalOpen(false)
        },
      }
    )
  }

  /* ---------------------------------- Doms ---------------------------------- */
  return (
    <App>
      <ContentSection loading={isLoadingQuery}>
        <Stack space={16}>
          <Stack
            direction="row"
            space={16}
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Input
              placeholder="Search by name or ID..."
              onChange={onFilterTextChange}
              prefix={<SearchOutlined />}
              style={{ width: '300px' }}
            />
            <Stack direction="row" space={8}>
              <Button
                type="primary"
                icon={<CalendarOutlined />}
                onClick={() => setIsModalOpen(true)}
                disabled={!selectedRows.length}
              >
                Edit Dates{' '}
                {selectedRows.length ? `(${selectedRows.length})` : ''}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isRefetching}
              />
            </Stack>
          </Stack>
          <EnrollmentOutstandingTable
            members={members}
            total={total}
            totalLabel="enrolled"
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            onGridReady={onGridReady}
            quickFilterText={searchText}
          />
        </Stack>

        <EditEnrollmentDatesModal
          open={isModalOpen}
          mode="subset"
          enrollment={enrollment}
          affectedCount={selectedRows.length}
          isSubmitting={isPending}
          serverError={mutationError?.message}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
        />
      </ContentSection>
    </App>
  )
}
